/**
 * RecipientsList
 * Renders a parent/child checkbox UI for selecting email recipients per site.
 * Parent checkbox selects/deselects all emails for a site.
 * Child checkboxes toggle individual emails within a collapsible panel per site.
 * State and data flow:
 * siteEmailMapSourceOfTruth provides the full set of emails per site.
 * siteEmailMapState provides the currently selected emails per site.
 * Parent/child changes are delegated via callbacks to update upstream state.
 * @param {Object} props - Component props.
 * @param {Object<string, {siteName: string, emails: Array<{EmailObjectId: string, EmailAddress: string}>}>} props.siteEmailMapState
 * Current selection map keyed by siteId, containing siteName and the selected emails array.
 * @param {Object<string, {siteName: string, emails: Array<{EmailObjectId: string, EmailAddress: string}>}>} props.siteEmailMapSourceOfTruth
 * Full source map keyed by siteId, containing siteName and all available emails for that site.
 * @param {(siteId: string, checkedValues: string[]) => void} props.onChildCheckboxChange
 * Callback invoked when a site's child CheckboxGroup changes; checkedValues is an array of EmailObjectId.
 * @param {(e: import('react').ChangeEvent<HTMLInputElement>, siteId: string) => void} props.onParentCheckboxChange
 * Callback invoked when a site's parent checkbox changes; should select all or clear all for that site.
 * @returns {JSX.Element} A scrollable card containing collapsible panels per site with parent/child checkboxes.
 */

import { Avatar, Checkbox, Col, Collapse, Row, Space, Typography, Card } from "antd";
import { UserOutlined } from "@ant-design/icons";

const { Text } = Typography;
const CheckboxGroup = Checkbox.Group;
const { Panel } = Collapse;

function RecipientsList({
  siteEmailMapState,
  onChildCheckboxChange,
  onParentCheckboxChange,
  siteEmailMapSourceOfTruth
}) {
  const siteIds = Object.keys(siteEmailMapSourceOfTruth || {});
  const stateSiteIds = Object.keys(siteEmailMapState || {});

  return (
    <Card className="recipients-list-card">
      {stateSiteIds.length > 0 &&
        siteIds.map(siteId => {
          const { siteName: sourceSiteName, emails: sourceEmails } =
            siteEmailMapSourceOfTruth[siteId];
          const { emails: stateEmails } = siteEmailMapState[siteId];

          const selectableIds = stateEmails.map(e => e.EmailObjectId);
          const allIds = sourceEmails.map(e => e.EmailObjectId);

          const checkAll = selectableIds.length === allIds.length && allIds.length > 0;
          const indeterminate = selectableIds.length > 0 && selectableIds.length < allIds.length;

          const renderEmailLine = email => (
            <div className="flex gap-2" key={`${siteId}-email-${email.EmailObjectId}`}>
              <Checkbox value={email.EmailObjectId} />
              <span className="table-td-color">{email.EmailAddress}</span>
            </div>
          );

          return (
            <Collapse
              key={siteId}
              collapsible="icon"
              className="collapsable-site pt-1 pb-1 bg-transparent rounded-none border-0 border-b border-gray"
            >
              <Panel
                header={
                  <Row>
                    <Col span={23}>
                      <Space size="small">
                        <Avatar
                          style={{ backgroundColor: "green", verticalAlign: "middle" }}
                          size="small"
                        >
                          <UserOutlined />
                        </Avatar>
                        <Checkbox
                          indeterminate={indeterminate}
                          checked={checkAll}
                          onChange={e => onParentCheckboxChange(e, siteId)}
                        />
                        <Text level={5} style={{ margin: 0 }}>
                          {sourceSiteName}
                        </Text>
                      </Space>
                    </Col>
                  </Row>
                }
                key={`${siteId}-panel`}
              >
                <CheckboxGroup
                  className="flex flex-col"
                  value={selectableIds}
                  onChange={vals => onChildCheckboxChange(siteId, vals)}
                >
                  {sourceEmails.map(renderEmailLine)}
                </CheckboxGroup>
              </Panel>
            </Collapse>
          );
        })}
    </Card>
  );
}

export default RecipientsList;